import { Link } from "react-router-dom";
import { Check, ArrowRight, Sparkles, Calendar, Mail, Download } from "lucide-react";
import { SERVICES } from "../lib/brand";

export default function Services() {
  return (
    <div data-testid="page-services">
      {/* Header */}
      <section className="pt-16 pb-12 sm:pt-24 sm:pb-16 bg-mint-soft relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-40" />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <p className="text-sm uppercase tracking-[0.25em] text-lilac-deep font-semibold reveal">
            The Services
          </p>
          <h1 className="reveal reveal-d1 mt-5 font-serif-display text-5xl sm:text-6xl lg:text-7xl font-bold text-ink leading-[1.02] tracking-tight">
            Two doors in.
            <span className="block italic text-lilac-deep">Same destination.</span>
          </h1>
          <p className="reveal reveal-d2 mt-6 text-lg sm:text-xl text-charcoal max-w-2xl mx-auto leading-relaxed">
            Choose the entry that fits your week. Whichever you pick — you walk
            out with a system you'll quietly use for the rest of your life.
          </p>
        </div>
      </section>

      {/* Service cards — full detail */}
      <section className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-8 lg:gap-10">
          {SERVICES.map((s, i) => (
            <article
              key={s.id}
              data-testid={`services-card-${s.id}`}
              className={`relative rounded-3xl overflow-hidden border ${
                i === 0 ? "border-lilac bg-white" : "border-mint bg-mint-soft/50"
              } p-8 sm:p-10 flex flex-col`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className={`p-3 rounded-2xl ${i === 0 ? "bg-lilac-soft" : "bg-white"}`}>
                  {i === 0 ? (
                    <Calendar className="text-lilac-deep" size={24} />
                  ) : (
                    <Download className="text-mint-deep" size={24} />
                  )}
                </div>
                <span className="px-3 py-1 rounded-full bg-ink text-white text-[10px] uppercase tracking-widest font-semibold">
                  {s.badge}
                </span>
              </div>

              <h2 className="mt-7 font-serif-display text-3xl sm:text-4xl font-bold text-ink leading-[1.1]">
                {s.name}
              </h2>
              <p className="mt-4 text-charcoal leading-relaxed">{s.tagline}</p>

              <div className="mt-8 flex items-baseline gap-3">
                <span className="font-serif-display text-6xl sm:text-7xl font-bold text-ink leading-none">
                  {s.priceLabel}
                </span>
              </div>
              <div className="text-xs uppercase tracking-widest text-charcoal mt-2">
                {s.priceNote}
              </div>

              <ul className="mt-8 space-y-3.5">
                {s.bullets.map((b, idx) => (
                  <li key={idx} className="flex gap-3 text-[15px] text-ink/90 leading-relaxed">
                    <span
                      className={`shrink-0 h-6 w-6 rounded-full flex items-center justify-center mt-0.5 ${
                        i === 0 ? "bg-lilac-soft text-lilac-deep" : "bg-white text-mint-deep"
                      }`}
                    >
                      <Check size={14} strokeWidth={3} />
                    </span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-9 pt-9 border-t border-ink/10 flex items-center justify-between gap-4">
                <div className="text-xs uppercase tracking-widest text-charcoal">
                  {s.duration}
                </div>
                <Link
                  to="/book"
                  data-testid={`services-cta-${s.id}`}
                  className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
                >
                  Book this <ArrowRight size={15} />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Comparison table */}
      <section data-testid="comparison-section" className="pb-20 sm:pb-28">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-[0.2em] text-lilac-deep font-semibold">
              Side by side
            </p>
            <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl font-bold text-ink">
              Which feels like you?
            </h2>
          </div>

          <div className="rounded-3xl border border-border bg-white overflow-hidden">
            <div className="grid grid-cols-3 text-sm">
              <div className="p-5 sm:p-6 bg-cream font-semibold text-ink">Compare</div>
              <div className="p-5 sm:p-6 bg-lilac-soft font-serif-display text-lg text-lilac-deep">
                2-Hour Course
              </div>
              <div className="p-5 sm:p-6 bg-mint-soft font-serif-display text-lg text-mint-deep">
                Digital Vault
              </div>
              {[
                ["Best for", "Hands-on learners who want a real session", "Self-starters who move fast"],
                ["Time commitment", "2 hours live + replay", "20 minutes to deploy"],
                ["Format", "Live walkthrough with Toni", "Instant download to inbox"],
                ["Includes Calendar", "Yes", "Yes — the master rotation"],
                ["Lifetime updates", "Yes", "Yes"],
                ["Price", "$20", "From $27"],
              ].map((row, idx) => (
                <div key={idx} className="contents">
                  <div className="p-5 sm:p-6 border-t border-border font-medium text-charcoal">
                    {row[0]}
                  </div>
                  <div className="p-5 sm:p-6 border-t border-border text-ink">{row[1]}</div>
                  <div className="p-5 sm:p-6 border-t border-border text-ink">{row[2]}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pb-24 sm:pb-32">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="rounded-[2rem] bg-ink text-white p-10 sm:p-16 text-center relative overflow-hidden">
            <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-lilac-deep/40 blur-3xl" />
            <div className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-mint-deep/30 blur-3xl" />
            <div className="relative">
              <Sparkles className="mx-auto text-mint" size={28} />
              <h2 className="mt-5 font-serif-display text-3xl sm:text-5xl font-bold leading-[1.05]">
                Not sure which one?
                <span className="block italic text-mint">Let me help you pick.</span>
              </h2>
              <p className="mt-5 text-white/75 max-w-xl mx-auto">
                A 30-minute call is the fastest way to know — and it's free.
              </p>
              <Link
                to="/book"
                data-testid="services-final-cta"
                className="mt-9 inline-flex items-center gap-2 rounded-full px-7 py-3.5 bg-mint text-ink font-semibold hover:bg-mint/85 transition-colors"
              >
                Book a free 30-min call <ArrowRight size={18} />
              </Link>
              <div className="mt-6 text-xs text-white/55 flex items-center justify-center gap-2">
                <Mail size={14} /> or write to hello@sixfigurecouponer.com
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
